// src/features/reits-ranking/components/UniverseTable.tsx
//
// Data-dense, client-sortable leaderboard for the FULL REIT universe.
// Real-Estate sibling of features/etf-ranking/components/UniverseTable.
// It reuses the shared heat-coloured z-score badge (<ScoreBadge>) and the
// equity column contract (the REIT scorer emits the equity OUTPUT_SCHEMA):
// `sector`, `fundamental_z_score` (surfaced as "Quality") and the
// composite `forge_ultra_signal`. It adds:
//   - click-to-sort column headers with a direction caret,
//   - a 90-day Forge Ultra Signal sparkline column (data is fetched in one
//     batched request by the parent and passed down as a map),
//   - row-hover callback so the parent can cross-highlight the matching
//     dot on the Risk vs Return scatter.
//
// Sorting happens entirely in the parent (it owns the already-sorted
// array); this component is purely presentational and just renders rows +
// reports header clicks.

import { ScoreBadge } from '../../../shared/ui/RankingTable';
import type { HistoryPoint, UniverseRow } from '../api';
import { Sparkline } from './Sparkline';

// Columns the user can sort by. ``label`` is the header text; ``align``
// drives the cell alignment so numeric columns stay right/centre.
export type SortKey =
    | 'universe_rank'
    | 'symbol'
    | 'sector'
    | 'forge_ultra_signal'
    | 'momentum_z_score'
    | 'fundamental_z_score'
    | 'sentiment_z_score'
    | 'annualized_volatility';

interface UniverseTableProps {
    data: UniverseRow[];
    sparklines?: Record<string, HistoryPoint[]>;
    sortKey: SortKey;
    sortDesc: boolean;
    onSort: (key: SortKey) => void;
    onRowHover?: (symbol: string | null) => void;
    /** Symbols for which a sparkline was requested (so we can show a
     *  subtle "loading" placeholder for rows still awaiting data). */
    sparklineSymbols?: Set<string>;
    /** Fired when a ticker is clicked — opens the symbol quick-stats
     *  pop-over in the parent. */
    onSymbolClick?: (symbol: string) => void;
}

interface ColumnDef {
    key: SortKey;
    label: string;
    align: 'left' | 'center' | 'right';
    sortable: boolean;
}

const COLUMNS: ColumnDef[] = [
    { key: 'universe_rank', label: 'Rank', align: 'left', sortable: true },
    { key: 'symbol', label: 'Symbol', align: 'left', sortable: true },
    { key: 'sector', label: 'Sector', align: 'left', sortable: true },
    {
        key: 'forge_ultra_signal',
        label: 'Forge Ultra',
        align: 'right',
        sortable: true,
    },
    {
        key: 'momentum_z_score',
        label: 'Momentum (z)',
        align: 'center',
        sortable: true,
    },
    {
        key: 'fundamental_z_score',
        label: 'Quality (z)',
        align: 'center',
        sortable: true,
    },
    {
        key: 'sentiment_z_score',
        label: 'Sentiment (z)',
        align: 'center',
        sortable: true,
    },
    {
        key: 'annualized_volatility',
        label: 'Vol (Ann.)',
        align: 'right',
        sortable: true,
    },
];

const alignClass = (align: ColumnDef['align']): string =>
    align === 'right'
        ? 'text-right'
        : align === 'center'
          ? 'text-center'
          : 'text-left';

export const UniverseTable = ({
    data,
    sparklines,
    sortKey,
    sortDesc,
    onSort,
    onRowHover,
    sparklineSymbols,
    onSymbolClick,
}: UniverseTableProps) => {
    return (
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase bg-slate-50 dark:bg-slate-950/50 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                    <tr>
                        {COLUMNS.map((col) => {
                            const isActive = sortKey === col.key;
                            return (
                                <th
                                    key={col.key}
                                    scope="col"
                                    className={`px-6 py-4 font-semibold ${alignClass(col.align)} ${
                                        col.sortable
                                            ? 'cursor-pointer select-none hover:text-slate-700 dark:hover:text-slate-200'
                                            : ''
                                    }`}
                                    onClick={
                                        col.sortable
                                            ? () => onSort(col.key)
                                            : undefined
                                    }
                                    aria-sort={
                                        isActive
                                            ? sortDesc
                                                ? 'descending'
                                                : 'ascending'
                                            : undefined
                                    }
                                >
                                    {col.label}
                                    {isActive && (
                                        <span className="ml-1 text-green-600 dark:text-emerald-400">
                                            {sortDesc ? '▼' : '▲'}
                                        </span>
                                    )}
                                </th>
                            );
                        })}
                        <th
                            scope="col"
                            className="px-6 py-4 font-semibold text-center"
                        >
                            90D Trend
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {data.length === 0 ? (
                        <tr>
                            <td
                                colSpan={COLUMNS.length + 1}
                                className="px-6 py-16 text-center text-slate-500 dark:text-slate-400"
                            >
                                No REIT ranking data available yet. The
                                nightly scoring run may still be pending.
                            </td>
                        </tr>
                    ) : (
                        data.map((row) => {
                            const series = sparklines?.[row.symbol];
                            const awaiting =
                                sparklineSymbols?.has(row.symbol) &&
                                series === undefined;
                            return (
                                <tr
                                    key={row.symbol}
                                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                    onMouseEnter={() => onRowHover?.(row.symbol)}
                                    onMouseLeave={() => onRowHover?.(null)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap font-bold text-slate-900 dark:text-white">
                                        #{row.universe_rank}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-bold text-green-600 dark:text-emerald-400">
                                        {onSymbolClick ? (
                                            <button
                                                type="button"
                                                className="font-bold text-green-600 dark:text-emerald-400 hover:underline focus:outline-none focus:underline cursor-pointer"
                                                onClick={() =>
                                                    onSymbolClick(row.symbol)
                                                }
                                            >
                                                {row.symbol}
                                            </button>
                                        ) : (
                                            row.symbol
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-slate-600 dark:text-slate-400">
                                        {row.sector}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-slate-900 dark:text-white">
                                        {row.forge_ultra_signal.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <ScoreBadge score={row.momentum_z_score} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <ScoreBadge
                                            score={row.fundamental_z_score}
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <ScoreBadge
                                            score={row.sentiment_z_score}
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-slate-500 dark:text-slate-400">
                                        {(row.annualized_volatility * 100).toFixed(1)}%
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {awaiting ? (
                                            <span className="inline-block h-7 w-24 rounded bg-slate-100 dark:bg-slate-800 animate-pulse" />
                                        ) : (
                                            <Sparkline series={series ?? []} />
                                        )}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default UniverseTable;
